import { useState } from "react";
import { feesApi, ApiError } from "@/api";
import type { Fee, JobApplication, FeeKind, FeeCreateInput, FeeUpdateInput } from "@/types";
import { Button, Pill, TextField, TextAreaField, SelectField } from "@/components/ui";
import { feeStatusTone, humanise } from "@/lib/jobs";
import { formatMoney } from "@/lib/money";
import { FormDrawer } from "./FormDrawer";

const TYPES: { value: FeeKind; label: string; note: string }[] = [
  { value: "service", label: "Service", note: "VAT applies" },
  { value: "disbursement", label: "Disbursement", note: "no VAT" },
];

/** Create or edit a fee (Brief 04 §3.3–3.4). On edit, amount_invoiced/paid,
 *  status and the linked application are read-only; the amount input is floored
 *  at amount_invoiced (server is the source of truth). */
export function FeeDrawer({
  jobId,
  fee,
  applications,
  onClose,
  onSaved,
  onDeleted,
}: {
  jobId: number;
  fee: Fee | null; // null = create
  applications: JobApplication[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}) {
  const isEdit = fee != null;
  const [description, setDescription] = useState(fee?.description ?? "");
  const [amount, setAmount] = useState(fee != null ? String(fee.amount) : "");
  const [feeType, setFeeType] = useState<FeeKind>(fee?.fee_type ?? "service");
  const [appId, setAppId] = useState<number>(fee?.application_id ?? 0);
  const [notes, setNotes] = useState(fee?.notes ?? "");

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const invoiced = fee?.amount_invoiced ?? 0;
  const billed = invoiced > 0; // billed fees can't be deleted (server enforces)

  async function del() {
    if (!fee || saving || deleting) return;
    if (billed) {
      setError("Billed fees can't be deleted.");
      return;
    }
    if (!window.confirm(`Delete the fee “${fee.description}”? This can't be undone.`)) return;
    setDeleting(true);
    setError(undefined);
    try {
      await feesApi.deleteFee(fee.id);
      onDeleted?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the fee.");
      setDeleting(false);
    }
  }
  const linkedApp = isEdit && fee.application_id > 0
    ? applications.find((a) => a.id === fee.application_id)
    : undefined;

  async function submit() {
    if (saving) return;
    const amt = Number(amount);
    if (!description.trim()) {
      setError("Enter a description.");
      return;
    }
    if (!amount.trim() || Number.isNaN(amt) || amt <= 0) {
      setError("Enter an amount greater than zero.");
      return;
    }
    if (isEdit && amt < invoiced) {
      setError(`Amount can't go below ${formatMoney(invoiced)} already invoiced.`);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      if (isEdit && fee) {
        const patch: FeeUpdateInput = {
          description: description.trim(),
          amount: amt,
          fee_type: feeType,
          notes: notes.trim(),
        };
        await feesApi.updateFee(fee.id, patch);
      } else {
        const input: FeeCreateInput = {
          job_id: jobId,
          description: description.trim(),
          amount: amt,
          fee_type: feeType,
          notes: notes.trim() || undefined,
        };
        if (appId > 0) input.application_id = appId;
        await feesApi.createFee(input);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the fee.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title={isEdit ? "Edit fee" : "New fee"}
      busy={saving || deleting}
      onClose={onClose}
      footer={
        <>
          {isEdit ? (
            <Button
              variant="danger"
              className="job-task-del"
              onClick={del}
              loading={deleting}
              disabled={saving || billed}
              title={billed ? "Billed fees can't be deleted" : undefined}
            >
              Delete fee
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={saving || deleting}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={deleting}>{isEdit ? "Save fee" : "Add fee"}</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <TextField label="Description" required value={description} onChange={(e) => setDescription(e.target.value)} />
        <TextField
          label="Amount (£)"
          required
          type="number"
          step="0.01"
          min={isEdit ? invoiced : 0}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={isEdit && invoiced > 0 ? `Can't go below ${formatMoney(invoiced)} already invoiced.` : undefined}
        />

        <div className="field">
          <span className="field__label">Type</span>
          <div className="job-seg" role="tablist" aria-label="Fee type">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={feeType === t.value}
                className={`job-seg__btn${feeType === t.value ? " is-on" : ""}`}
                onClick={() => setFeeType(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="field__desc">{feeType === "service" ? "Service — VAT applies" : "Disbursement — no VAT"}</p>
        </div>

        {isEdit ? (
          linkedApp || (fee && fee.application_id > 0) ? (
            <p className="job-drawer__muted">
              Linked application: {linkedApp?.app_reference || `Application #${fee?.application_id}`} (set at create — not changeable)
            </p>
          ) : null
        ) : applications.length > 0 ? (
          <SelectField
            label="Link to application (optional)"
            value={String(appId)}
            onChange={(e) => setAppId(Number(e.target.value))}
          >
            <option value="0">None / job-level</option>
            {applications.map((a) => (
              <option key={a.id} value={a.id}>
                {a.app_reference || `Draft #${a.id}`}{a.application_type ? ` — ${humanise(a.application_type)}` : ""}
              </option>
            ))}
          </SelectField>
        ) : null}

        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </section>

      {isEdit && fee ? (
        <section className="job-drawer__section">
          <h3>Invoicing</h3>
          <p className="job-drawer__muted">These are managed by the invoicing flow and can't be edited here.</p>
          <dl className="job-fee__sysfields">
            <div><dt>Invoiced</dt><dd>{formatMoney(fee.amount_invoiced)}</dd></div>
            <div><dt>Paid</dt><dd>{formatMoney(fee.amount_paid)}</dd></div>
            <div><dt>Status</dt><dd><Pill tone={feeStatusTone(fee.status)}>{humanise(fee.status)}</Pill></dd></div>
          </dl>
        </section>
      ) : null}
    </FormDrawer>
  );
}
